<template>
  <div id="app">

    <div class="pub-header-content">
      <ArticleHeader title='Precision genome editing using cytosine and adenine base editors' />
      <Authors />
      <ArticleAbstract />
      <hr>
    </div>
     
    <div>
      <div class="section data-card-table">

        <!-- header and dropdown -->
        <div class="flex-grid">
          <div class="col-12">
            <h1>What type of base editor is best for your project?</h1>
            <h3>Click on a card below to filter the tables by desired properties, or use the dropdown menu.</h3>
          </div>
        </div>

        <div>
          <div class="button-holder" >
            <select v-model="selectedVar" v-on:change="scrollFromDropdown">
              <option disabled value="">Please select one</option>
              <option value="ALL">All Base Editors</option>
              <option value="D">Reduced DNA off-target</option>
              <option value="E">Expanded-window</option>
              <option value="C">Context-specific</option>
              <option value="H">High activity</option>
              <option value="M">Nonspecific mutagenic</option>
              <option value="N">Narrow-window</option>
              <option value="R">Reduced RNA off-target</option>
              <option value="S">Standard</option>
            </select>
          </div>
        </div>

        <!-- start displaying cards -->
        <div class="flex-grid">

          <!-- left section -->
          <div class="col-7 column">
            <h2 class="group-title">Precise allele substitution</h2>
            <hr>
            <div class="target-locus-card">
              <img src="./assets/nav-cards/precisegraphic.svg" alt="">
            </div>
            <h3 class="card-title">
              Maximize editing efficiency
            </h3>
            <div class="flex-grid">
              <div class="col-6 grid-cell">
                <a href="#base-editor-tables" v-smooth-scroll>
                  <img @click="applyFilter(`S`)"
                  :class="selectedVar == `S` || selectedVar == `ALL` ? 'active-card' : 'inactive-card'"
                  class="nav-card" 
                  src="./assets/nav-cards/S.png" 
                  alt=""
                  >
                </a>
              </div>
              <div class="col-6 grid-cell">
                <a href="#base-editor-tables" v-smooth-scroll>
                <img @click="applyFilter(`H`)" 
                  :class="selectedVar == `H` || selectedVar == `ALL` ? 'active-card' : 'inactive-card'"
                  class="nav-card" 
                  src="./assets/nav-cards/H.png" 
                  alt="">
                </a>
              </div>
            </div>

            <h3 class="card-title">
              Minimize bystander editing
            </h3>
            <div class="flex-grid">
              <div class="col-6 grid-cell">
                <a href="#base-editor-tables" v-smooth-scroll>
                  <img @click="applyFilter(`N`)" 
                  :class="selectedVar == `N` || selectedVar == `ALL` ? 'active-card' : 'inactive-card'"
                  class="nav-card" 
                  src="./assets/nav-cards/N.png">
                  
                </a>
              </div>
              <div class="col-6 grid-cell">
                <a href="#base-editor-tables" v-smooth-scroll>
                  <img @click="applyFilter(`C`)" 
                  :class="selectedVar == `C` || selectedVar == `ALL` ? 'active-card' : 'inactive-card'"
                  class="nav-card" 
                  src="./assets/nav-cards/C.png" 
                  alt="">
                </a>
              </div>
            </div>

            <h3 class="card-title">
              Minimize off-target editing
            </h3>
            <div class="flex-grid">
              <div class="col-6 grid-cell">
                <a href="#base-editor-tables" v-smooth-scroll>
                  <img @click="applyFilter(`D`)" 
                  :class="selectedVar == `D` || selectedVar == `ALL` ? 'active-card' : 'inactive-card'"
                  class="nav-card" 
                  src="./assets/nav-cards/D.png" 
                  alt="">
                </a>
              </div>
              <div class="col-6 grid-cell">
                <a href="#base-editor-tables" v-smooth-scroll>
                  <img @click="applyFilter(`R`)" 
                  :class="selectedVar == `R` || selectedVar == `ALL` ? 'active-card' : 'inactive-card'"
                  class="nav-card" 
                  src="./assets/nav-cards/R.png" 
                  alt="">
                </a>
              </div>
            </div>
          </div>

          <!-- right section -->
          <div class="col-5 column">
            <h2 class="group-title">Broad mutagenesis of target region  </h2>
            <hr>
            <img class="target-locus-card" src="./assets/nav-cards/broadgraphic.svg" alt="">

            <div class="flex-grid">
              <div class="col-12 grid-cell">
                <a href="#base-editor-tables" v-smooth-scroll>
                  <img @click="applyFilter(`E`)" 
                  :class="selectedVar == `E` || selectedVar == `ALL` ? 'active-card' : 'inactive-card'"
                  class="nav-card" 
                  id="card-E"
                  src="./assets/nav-cards/E.png" 
                  alt="">
                </a>
              </div>
            </div>
            <div class="flex-grid">
              <div class="col-12 grid-cell">
                <a href="#base-editor-tables" v-smooth-scroll>
                  <img @click="applyFilter(`M`)"
                  :class="selectedVar == `M` || selectedVar == `ALL` ? 'active-card' : 'inactive-card'"
                  class="nav-card" 
                  src="./assets/nav-cards/M.png" 
                  alt="">
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>
    <div>
      <div class="table-breadcrumb">
        <h2>{{selectionDisplayName}}</h2>
      </div>
    </div>

    <!-- TABBED TABLES -->
    <TabCard 
    :tabs="tabs" 
    :initialTab="initialTab" 
    class="section tab-table" 
    id="base-editor-tables"
    >
      <template slot="tab-head-cas9">
        Cas variants
      </template>
      <template slot="tab-panel-cas9">
        <object
          :data="getImgUrl(`tables/cas9/Cas9variants_`+selectedVar+`.svg`)"
          alt=""
        />
      </template>
      <template slot="tab-head-deam">
        Deaminases
      </template>
      <template slot="tab-panel-deam">
        <object type="image/svg+xml" :data="getImgUrl(`tables/deam/Deaminasevariants_`+selectedVar+`.svg`)"></object>
      </template>
      <template slot="tab-head-arch">
        Architecture
      </template>
      <template slot="tab-panel-arch">
        <object
          :data="getImgUrl(`tables/arch/Architectures_`+selectedVar+`.svg`)"
          alt=""
        />
      </template>
    </TabCard>
    </div>
  </div>
</template>

<script>
import ArticleHeader from './components/ArticleHeader.vue';
import Authors from './components/Authors.vue';
import TabCard from './components/TabCard.vue';
import ArticleAbstract from './components/ArticleAbstract.vue';

export default {
  name: 'App',
  components: {
    ArticleHeader,
    Authors,
    ArticleAbstract,
    TabCard
  },
  data() {
    return {
      initialTab: 'cas9',
      tabs: ['cas9', 'deam', 'arch'],
      selectedVar: "ALL",
      isActive: false
    }
  },
  computed: {
    selectionDisplayName() {
      const displayConversion = {
        "ALL": 'All base editors',
        "D": 'Reduced DNA off-target',
        "C": 'Context-specific',
        "E": 'Expanded-window',
        "H": 'High-activity',
        "M": 'Nonspecific mutagenic',
        "N": 'Narrow-window',
        "R": 'Reduced RNA off-target',
        "S": 'Standard'
      }

      return displayConversion[this.selectedVar];
    }
  },
  methods: {
    applyFilter(val) {
      if (val == 'ALL') {
        this.selectedVar = 'ALL';
      } else {
        this.selectedVar = val;
      }
      
      this.isActive = !this.isActive;
    },
    getImgUrl(img) {
      return require('./assets/'+img);
    },
    scrollFromDropdown() {
      this.$smoothScroll({
        scrollTo: document.getElementById("base-editor-tables"),
        hash: "#base-editor-tables",
        duration: 1000,
        offset: -80,
      })
    }
  }
}
</script>

<style scoped>
  text {
    font-family: 'Lato', sans-serif
  }
</style>
